import { HardwareService } from '../../HardwareService';
import type { BarcodeData } from '../types';
import { resolvePdf417DecoderWithin } from './decoders/resolve';

/** Matches the 4MB the apps already allow for camera-roll photos; drawing more blocks the thread. */
export const MAX_UPLOAD_IMAGE_BYTES = 4 * 1024 * 1024;

/** Budgets resolving the decoder, because the shared wasm fetch behind it has no timeout of its own. */
const DECODER_RESOLVE_BUDGET_MS = 30_000;

export type ImageDecodeOutcome = {
  barcode: BarcodeData | null;
  decoder: string | null;
  /** The file could not be read as an image at all — HEIC on a non-Apple browser, or corrupt. */
  unsupported: boolean;
  elapsedMs: number;
  /** Width actually decoded, for comparison against the reliable-width guidance. */
  widthPx: number;
  /**
   * The decoder never arrived inside the budget, or the caller cancelled. Distinct from `decoder:
   * null`, which means this device has no PDF417 decoder at all: only one of the two is worth retrying.
   */
  gaveUp?: boolean;
};

export type ImageDecodeOptions = {
  /** Caps resolving the decoder, not the decode itself. Defaults to DECODER_RESOLVE_BUDGET_MS. */
  timeoutMs?: number;
  /** Lets a caller that has closed the screen stop waiting on a decoder that never arrives. */
  signal?: AbortSignal;
};

const outcome = (startedAt: number, fields: Partial<ImageDecodeOutcome> = {}): ImageDecodeOutcome => ({
  barcode: null,
  decoder: null,
  unsupported: false,
  widthPx: 0,
  elapsedMs: Date.now() - startedAt,
  ...fields,
});

/** Valid per spec; this package's DOM lib predates the value. */
const FROM_IMAGE = { imageOrientation: 'from-image' } as unknown as ImageBitmapOptions;

/** EXIF orientation applied where the browser supports it; the decoder's `tryRotate` covers the rest. */
const toBitmap = async (file: Blob): Promise<ImageBitmap | null> => {
  if (typeof createImageBitmap !== 'function') {
    return null;
  }

  try {
    return await createImageBitmap(file, FROM_IMAGE);
  } catch {
    try {
      return await createImageBitmap(file);
    } catch {
      return null;
    }
  }
};

/** Client-side decode of a still. One attempt, so it succeeds far less often than a live preview. */
export const decodePdf417FromImage = async (
  file: Blob,
  options: ImageDecodeOptions = {},
): Promise<ImageDecodeOutcome> => {
  const startedAt = Date.now();
  const timeoutMs = options.timeoutMs ?? DECODER_RESOLVE_BUDGET_MS;
  const resolution = await resolvePdf417DecoderWithin(timeoutMs, options.signal);
  if (resolution.gaveUp) {
    // Logged as an error because a stalled fetch here is invisible otherwise, and reported as
    // `gaveUp` so a caller can say "try again" rather than "this device cannot scan".
    HardwareService.logger.e('PDF417 decoder did not arrive in time to read the uploaded image', {
      timeoutMs,
      aborted: options.signal?.aborted === true,
    });
    return outcome(startedAt, { gaveUp: true });
  }

  const decoder = resolution.decoder;
  if (!decoder) {
    // Visible on purpose: the previous silence read to the operator as an unreadable photo.
    // `decoder: null` is already how both callers choose ID_SCAN_NO_DECODER_MESSAGE.
    HardwareService.logger.e('PDF417 decoder unavailable; cannot read the uploaded image', {
      reason: 'the ladder resolved no decoder',
    });
    return outcome(startedAt);
  }

  const bitmap = await toBitmap(file);
  if (!bitmap) {
    return outcome(startedAt, { decoder: decoder.name, unsupported: true });
  }

  let canvas: HTMLCanvasElement | null = document.createElement('canvas');
  try {
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) {
      return outcome(startedAt, { decoder: decoder.name, unsupported: true });
    }

    context.drawImage(bitmap, 0, 0);
    const barcode = await decoder.decode(context.getImageData(0, 0, bitmap.width, bitmap.height));

    return outcome(startedAt, { barcode, decoder: decoder.name, widthPx: bitmap.width });
  } finally {
    bitmap.close();
    canvas.width = 0;
    canvas.height = 0;
    canvas = null;
  }
};

/** Refuses anything that is not an image, or is over the shared ceiling. */
export const isDecodableImageFile = (file: File): boolean =>
  file.type.startsWith('image/') && file.size > 0 && file.size <= MAX_UPLOAD_IMAGE_BYTES;
